import { useCallback, useState } from "react";
import { useImportFromContactOut } from "@/features/contacts/hooks/useApollo";
import { importSucceeded, linkedinProfileKey } from "../utils/prospectUtils";
import type { ProspectItem } from "../types";

type Id = string | number;

const addIds = (set: Set<Id>, ids: Id[]) => {
  const next = new Set(set);
  ids.forEach((id) => next.add(id));
  return next;
};

const removeIds = (set: Set<Id>, ids: Id[]) => {
  const next = new Set(set);
  ids.forEach((id) => next.delete(id));
  return next;
};

/**
 * Imports prospects into Contacts. The import hook shows the result toasts; this
 * tracks which rows are importing and which really made it into Contacts.
 */
/** What the contact filters require of each imported person; the server enforces it too. */
export interface ContactRequirements {
  email: boolean;
  phone: boolean;
}

export function useProspectImport(required: ContactRequirements = { email: true, phone: false }) {
  const { mutateAsync: importContacts } = useImportFromContactOut();
  const [importedIds, setImportedIds] = useState<Set<Id>>(() => new Set());
  const [importingIds, setImportingIds] = useState<Set<Id>>(() => new Set());
  const [isBulkImporting, setIsBulkImporting] = useState(false);

  const runImport = useCallback(
    async (prospects: ProspectItem[], includePhone: boolean, campaignIds: Array<string | number> = []) => {
      const ids = prospects.map((p) => p.id).filter((id) => !importedIds.has(id));
      if (ids.length === 0) return false;
      const toImport = prospects.filter((p) => ids.indexOf(p.id) !== -1);

      setImportingIds((prev) => addIds(prev, ids));
      try {
        const data = await (importContacts as any)({
          prospects: toImport,
          campaign_ids: campaignIds.map(String),
          include_phone: includePhone,
          require_email: required.email,
          require_phone: required.phone,
        });
        if (!importSucceeded(data)) return false;
        // Rows the server left out (e.g. ContactOut had nothing for them) stay importable.
        const failed = new Set(
          (Array.isArray(data?.errors) ? data.errors : []).map((e: any) => linkedinProfileKey(e?.linkedin_url)),
        );
        const done = toImport.filter((p) => !failed.has(linkedinProfileKey(p.linkedin_url))).map((p) => p.id);
        setImportedIds((prev) => addIds(prev, done));
        return true;
      } catch {
        return false;
      } finally {
        setImportingIds((prev) => removeIds(prev, ids));
      }
    },
    [importContacts, importedIds, required.email, required.phone],
  );

  // Phone numbers cost extra lookups: they are fetched only when "Has Phone Number" is on.
  const importOne = useCallback(
    (prospect: ProspectItem, campaignIds: Array<string | number> = []) =>
      runImport([prospect], required.phone, campaignIds),
    [runImport, required.phone],
  );

  const importMany = useCallback(
    async (prospects: ProspectItem[], campaignIds: Array<string | number> = []) => {
      setIsBulkImporting(true);
      try {
        return await runImport(prospects, required.phone, campaignIds);
      } finally {
        setIsBulkImporting(false);
      }
    },
    [runImport, required.phone],
  );

  return {
    importedIds,
    importingIds,
    isBulkImporting,
    importOne,
    importMany,
  };
}
